<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-3xl' }">
        <UCard :ui="{ body: { padding: '' } }">
            <template #header>
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-base font-medium leading-6 text-gray-800">Movements</h3>
                        <p v-if="lot" class="text-sm text-gray-500 mt-0.5">
                            {{ lot.item?.name }}<span v-if="lot.lot_number"> · Lot <span class="font-mono">{{ lot.lot_number }}</span></span>
                        </p>
                    </div>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="$emit('update:open', false)" />
                </div>
            </template>

            <UTable
                :rows="movements"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-clock-counter-clockwise-duotone', label: 'No movements.' }"
                :ui="{ td: { padding: 'py-3' } }"
            >
                <template #created_at-data="{ row }">
                    <div class="leading-5">
                        <p>{{ dayjs(row.created_at).format('MMM D, YYYY HH:mm') }}</p>
                        <span v-if="row.creator" class="text-sm text-gray-500">{{ row.creator.name }}</span>
                    </div>
                </template>
                <template #reason-data="{ row }">
                    <div class="min-w-[10rem] whitespace-normal leading-5">
                        <p>{{ MOVEMENT_REASON_LABELS[row.reason as StockMovementReason] }}</p>
                        <span v-if="row.note" class="text-sm text-gray-500">{{ row.note }}</span>
                    </div>
                </template>
                <template #item-data="{ row }">
                    <div class="leading-5">
                        <p>{{ row.lot?.item?.name }}</p>
                        <span v-if="row.lot?.lot_number" class="font-mono text-sm text-gray-500">{{ row.lot.lot_number }}</span>
                    </div>
                </template>
                <template #delta-data="{ row }">
                    <p class="font-mono text-right">{{ row.delta > 0 ? '+' : '' }}{{ formatQty(row.delta) }}</p>
                </template>
            </UTable>
        </UCard>
    </USlideover>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { MOVEMENT_REASON_LABELS, type FieldPost, type StockLot, type StockMovement, type StockMovementReason } from '~/types/fieldPost'
import { formatQty } from '~/utils/fieldPosts'

const props = defineProps<{
    open: boolean
    post: FieldPost
    lot?: StockLot
}>()

defineEmits<{ 'update:open': [boolean] }>()

const client = useSupabaseClient()

const loading = ref(false)
const movements = ref<StockMovement[]>([])

const columns = computed(() => [
    { key: 'created_at', label: 'When' },
    { key: 'reason', label: 'Reason' },
    ...(props.lot ? [] : [{ key: 'item', label: 'Item / Lot' }]),
    { key: 'delta', label: 'Change', class: 'text-right' },
])

watch(() => props.open, async (open) => {
    if (!open) return
    loading.value = true
    movements.value = []
    let query = client
        .from('stock_movements')
        .select('*, lot:stock_lots!inner (post_id, lot_number, item:inventory_items (name, specification)), creator:users!stock_movements_created_by_fkey (id, name)')
        .eq('lot.post_id', props.post.id)
        .order('created_at', { ascending: false })
        .limit(200)
    if (props.lot) query = query.eq('lot_id', props.lot.id)
    const { data } = await query
    movements.value = (data ?? []) as StockMovement[]
    loading.value = false
})
</script>
